import { unitTotalHp } from "./stacks";
import { tileDef } from "./terrain";
import type {
  BuildingTileKind,
  GameState,
  PlayerId,
  ProductionBuildingKind,
  Tile,
  TileKind,
  Unit,
  UnitKind,
} from "./types";
import { isBuildingTile } from "./types";
import { MAX_HP, UNIT_DEFS, isAirUnit, isFootUnit } from "./units";

export const CAPTURE_FULL = 20;
export const INCOME_PER_PROPERTY = 100;
export const REPAIR_HP_RATE = 0.2;
export const MONUMENT_FIREPOWER_BONUS = 0.1;

export type { ProductionBuildingKind } from "./types";

export function isProductionBuilding(kind: TileKind): kind is ProductionBuildingKind {
  return kind === "barracks" || kind === "factory" || kind === "base" || kind === "airport";
}

export function isIncomeBuilding(kind: TileKind): boolean {
  return isBuildingTile(kind) && kind !== "monument";
}

export function hasActiveCapture(tile: Tile): boolean {
  return tile.capturePoints !== undefined && tile.capturePoints < CAPTURE_FULL;
}

export function tileDefensePercent(tile: Tile): number {
  return tileDef(tile.kind).defensePercent;
}

export function isCapturable(tile: Tile, owner: PlayerId): boolean {
  return isBuildingTile(tile.kind) && tile.owner !== owner;
}

export function canRepairOnTile(unitKind: UnitKind, tileKind: BuildingTileKind): boolean {
  if (unitKind === "supplyTruck") return false;
  if (tileKind === "monument") return false;
  if (isAirUnit(unitKind)) return tileKind === "airport";
  if (isFootUnit(unitKind)) {
    return tileKind === "city" || tileKind === "barracks" || tileKind === "base";
  }
  return tileKind === "city" || tileKind === "factory" || tileKind === "base";
}

export function unitCanRepairOnCurrentTile(state: GameState, unit: Unit): boolean {
  const tile = buildingAt(state, unit.pos);
  if (!tile || !isBuildingTile(tile.kind)) return false;
  if (tile.owner !== unit.owner) return false;
  return canRepairOnTile(unit.kind, tile.kind);
}

export function forecastRepairAfterDamage({
  unit,
  forecastedDamage,
}: {
  unit: Unit;
  forecastedDamage: number;
}): { hpRestored: number; repairCost: number; netDamage: number } {
  const targetHp = unitTotalHp(unit);
  if (forecastedDamage >= targetHp) {
    return { hpRestored: 0, repairCost: 0, netDamage: forecastedDamage };
  }

  const postAttackHp = targetHp - forecastedDamage;
  const maxHpStep = Math.round(MAX_HP * REPAIR_HP_RATE);
  const maxCostStep = Math.round(UNIT_DEFS[unit.kind].cost * REPAIR_HP_RATE);
  const hpRestored = Math.min(maxHpStep, MAX_HP - postAttackHp);
  if (hpRestored <= 0) {
    return { hpRestored: 0, repairCost: 0, netDamage: forecastedDamage };
  }

  const repairCost =
    hpRestored >= maxHpStep ? maxCostStep : Math.ceil((hpRestored / maxHpStep) * maxCostStep);
  return {
    hpRestored,
    repairCost,
    netDamage: Math.max(0, forecastedDamage - hpRestored),
  };
}

export type PlayerBuildingCounts = Record<
  PlayerId,
  {
    barracks: number;
    factories: number;
    bases: number;
    cities: number;
    airports: number;
    monuments: number;
  }
>;

export function countOwnedBuildingsPerPlayer(tiles: readonly Tile[]): PlayerBuildingCounts {
  const counts: PlayerBuildingCounts = {
    p1: { barracks: 0, factories: 0, bases: 0, cities: 0, airports: 0, monuments: 0 },
    p2: { barracks: 0, factories: 0, bases: 0, cities: 0, airports: 0, monuments: 0 },
  };
  for (const tile of tiles) {
    if (!isBuildingTile(tile.kind) || tile.owner === "neutral" || !tile.owner) continue;
    if (tile.kind === "barracks") counts[tile.owner].barracks += 1;
    else if (tile.kind === "factory") counts[tile.owner].factories += 1;
    else if (tile.kind === "base") counts[tile.owner].bases += 1;
    else if (tile.kind === "airport") counts[tile.owner].airports += 1;
    else if (tile.kind === "monument") counts[tile.owner].monuments += 1;
    else if (tile.kind === "city") counts[tile.owner].cities += 1;
  }
  return counts;
}

export function countOwnedTilesOfKind({
  tiles,
  kind,
  owner,
}: {
  tiles: readonly Tile[];
  kind: BuildingTileKind;
  owner: PlayerId;
}): number {
  return tiles.filter((tile) => tile.kind === kind && tile.owner === owner).length;
}

export function buildingAt(state: GameState, pos: { x: number; y: number }): Tile | undefined {
  const tile = state.tiles[pos.y * state.width + pos.x];
  if (tile && isBuildingTile(tile.kind)) return tile;
  return undefined;
}

function mapHasAirports(state: GameState): boolean {
  return state.tiles.some((tile) => tile.kind === "airport");
}

function hasEnemyAirUnits({ state, player }: { state: GameState; player: PlayerId }): boolean {
  return state.units.some((unit) => unit.owner !== player && isAirUnit(unit.kind));
}

export function isAntiAirBuildBlocked({
  state,
  kind,
  player,
}: {
  state: GameState;
  kind: UnitKind;
  player: PlayerId;
}): boolean {
  if (kind !== "missiles") return false;
  if (hasEnemyAirUnits({ state, player })) return false;
  return !mapHasAirports(state);
}
